import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useLanguage } from "../../i18n/LanguageContext";

export default function PracticeQuestionPage({
  currentIndex,
  questions = [],
  currentQuestion,

  practiceTopic,
  practiceSessionId,

  // Answers restored from practice_answers
  restoredAnswers = {},

  submitPracticeAnswer,
  submittingPracticeAnswer,

  goPrevious,
  goNext,
  finishRound,
  goBack,

  startSubscriptionCheckout,

  loading,
  styles,
}) {
  const { t, language } = useLanguage();

  // =========================================================
  // ANSWER STATE
  // =========================================================

  const [
    selectedOptionId,
    setSelectedOptionId,
  ] = useState(null);

  const [
    hasAnswered,
    setHasAnswered,
  ] = useState(false);

  const [
    isCorrect,
    setIsCorrect,
  ] = useState(false);

  const [
    correctOptionId,
    setCorrectOptionId,
  ] = useState(null);

  const [
    saveError,
    setSaveError,
  ] = useState("");

  // =========================================================
  // EXPLAIN STATE
  // =========================================================

  const [
    explanation,
    setExplanation,
  ] = useState("");

  const [
    explanationId,
    setExplanationId,
  ] = useState(null);

  const [
    explanationSource,
    setExplanationSource,
  ] = useState("");

  const [
    loadingExplanationLanguage,
    setLoadingExplanationLanguage,
  ] = useState(null);

  const [
    explanationError,
    setExplanationError,
  ] = useState("");

  const [
    explanationRating,
    setExplanationRating,
  ] = useState(null);

  const [
    submittingExplanationFeedback,
    setSubmittingExplanationFeedback,
  ] = useState(false);

  const [
    explanationFeedbackMessage,
    setExplanationFeedbackMessage,
  ] = useState("");

  const [
    explanationFeedbackError,
    setExplanationFeedbackError,
  ] = useState("");

  // =========================================================
  // RESTORE / RESET WHEN QUESTION CHANGES
  // =========================================================

  useEffect(() => {
    if (!currentQuestion) {
      return;
    }

    // -------------------------------------------------------
    // Always clear AI explanation when question changes
    // -------------------------------------------------------

    setExplanation("");
    setExplanationId(null);
    setExplanationSource("");
    setExplanationError("");
    setLoadingExplanationLanguage(null);
    setExplanationRating(null);
    setSubmittingExplanationFeedback(false);
    setExplanationFeedbackMessage("");
    setExplanationFeedbackError("");


    const restoredAnswer =
      restoredAnswers[
        currentQuestion.id
      ];


    // -------------------------------------------------------
    // Previously answered question
    // -------------------------------------------------------

    if (restoredAnswer) {
      setSelectedOptionId(
        restoredAnswer.selectedOptionId ||
          null
      );

      setHasAnswered(true);

      setIsCorrect(
        Boolean(
          restoredAnswer.isCorrect
        )
      );

      setCorrectOptionId(
        restoredAnswer.correctOptionId ||
          null
      );

      setSaveError("");

      return;
    }


    // -------------------------------------------------------
    // New unanswered question
    // -------------------------------------------------------

    setSelectedOptionId(null);

    setHasAnswered(false);

    setIsCorrect(false);

    setCorrectOptionId(null);

    setSaveError("");
  }, [
    currentQuestion,
    restoredAnswers,
  ]);

  // =========================================================
  // ANSWER
  // =========================================================

  const handleOptionClick = async (
    optionRecord
  ) => {
    if (
      hasAnswered ||
      submittingPracticeAnswer ||
      !currentQuestion ||
      !practiceSessionId
    ) {
      return;
    }

    setSelectedOptionId(
      optionRecord.id
    );

    setSaveError("");

    const result =
      await submitPracticeAnswer({
        practiceSessionId:
          practiceSessionId,

        questionId:
          currentQuestion.id,

        selectedOptionId:
          optionRecord.id,
      });

    if (!result?.success) {
      setSelectedOptionId(null);

      setSaveError(
        result?.error ||
          "Failed to submit answer."
      );

      return;
    }

    setHasAnswered(true);

    setIsCorrect(
      result.isCorrect
    );

    setCorrectOptionId(
      result.correctOptionId
    );
  };

  // =========================================================
  // EXPLAIN
  // =========================================================

  const renderExplanation = (text) => {
    if (!text) return null;

    const sectionTitles = [
      "Correct Answer:",
      "Knowledge Chain:",
      "Apply It to This Question:",
      "Option Analysis:",
      "Why Your Answer Was Tempting:",
      "Why Your Answer Works:",
      "Remember for the MBLEx:",
    ];

    let cleanedText = text
      .replace(/\*\*/g, "")
      .trim();

    sectionTitles.forEach((title) => {
      cleanedText = cleanedText.replace(
        new RegExp(title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
        `\n${title}\n`
      );
    });

    const lines = cleanedText
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);

    return (
      <div>
        {lines.map((line, index) => {
          const matchedTitle =
            sectionTitles.find((title) =>
              line.startsWith(title)
            );

          if (matchedTitle) {
            const restOfLine =
              line
                .slice(matchedTitle.length)
                .trim();

            return (
              <div
                key={index}
                style={{
                  marginTop:
                    index === 0 ? "0" : "20px",
                }}
              >
                <div
                  style={{
                    fontWeight: "800",
                    fontSize: "16px",
                    color: "#0f172a",
                    marginBottom: "6px",
                  }}
                >
                  {matchedTitle}
                </div>

                {restOfLine && (
                  <div
                    style={{
                      color: "#334155",
                      lineHeight: "1.7",
                    }}
                  >
                    {restOfLine}
                  </div>
                )}
              </div>
            );
          }

          return (
            <div
              key={index}
              style={{
                marginBottom: "8px",
                color: "#334155",
                lineHeight: "1.7",
              }}
            >
              {line}
            </div>
          );
        })}
      </div>
    );
  };
  
  const handleExplain = async (
    language = "english"
  ) => {
    if (
      !currentQuestion ||
      !hasAnswered
    ) {
      return;
    }

    try {
      setLoadingExplanationLanguage(language);
      setExplanationError("");

      const {
        data: {
          session: currentSession,
        },
      } =
        await supabase.auth.getSession();

      const accessToken =
        currentSession?.access_token;

      if (!accessToken) {
        setExplanationError(
          "Please log in again."
        );

        return;
      }

      const response =
        await fetch(
          "/api/explain",
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${accessToken}`,
            },

            body:
              JSON.stringify({
                questionId:
                  currentQuestion.id,

                language,
              }),
          }
        );

      const responseText =
        await response.text();

      let data;

      try {
        data =
          JSON.parse(responseText);
      } catch (error) {
        throw new Error(
          responseText ||
          "Explain API returned an invalid response."
        );
      }

      if (!response.ok) {
        throw new Error(
          data?.error ||
          "Unable to load explanation."
        );
      }

      setExplanation(
        data.explanation || ""
      );

      setExplanationId(
        data.explanationId || null
      );

      setExplanationSource(
        data.source || ""
      );

    } catch (error) {
      console.error(
        "PRACTICE EXPLAIN ERROR:",
        error
      );

      setExplanationError(
        error.message ||
        "Unable to load explanation."
      );
    } finally {
      setLoadingExplanationLanguage(null);
    }
  };

  // =========================================================
  // SUBMIT EXPLANATION FEEDBACK
  // =========================================================

  const handleExplanationFeedback =
    async (rating) => {
      if (
        !explanationId ||
        !currentQuestion?.id
      ) {
        return;
      }

      try {
        setSubmittingExplanationFeedback(
          true
        );

        setExplanationFeedbackError("");

        const {
          data: {
            session: currentSession,
          },
        } =
          await supabase.auth.getSession();

        const accessToken =
          currentSession?.access_token;

        if (!accessToken) {
          setExplanationFeedbackError(
            "Please log in again."
          );

          return;
        }

        const response =
          await fetch(
            "/api/explanation-feedback",
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${accessToken}`,
              },

              body:
                JSON.stringify({
                  explanationId,

                  questionId:
                    currentQuestion.id,

                  rating,
                }),
            }
          );


        const responseText =
          await response.text();

        let data;

        try {
          data =
            JSON.parse(
              responseText
            );
        } catch (error) {
          throw new Error(
            responseText ||
              "Feedback API returned an invalid response."
          );
        }


        if (!response.ok) {
          throw new Error(
            data?.error ||
              "Unable to save feedback."
          );
        }


        // -----------------------------------------------------
        // One user = one current rating.
        //
        // Clicking the other button later simply updates
        // the existing row through server-side upsert.
        // -----------------------------------------------------

        setExplanationRating(
          rating
        );

        setExplanationFeedbackMessage(
          t("practice.thanksFeedback")
        );
      } catch (error) {
        console.error(
          "EXPLANATION FEEDBACK ERROR:",
          error
        );

        setExplanationFeedbackError(
          error.message ||
            "Unable to save feedback."
        );
      } finally {
        setSubmittingExplanationFeedback(
          false
        );
      }
    };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div style={styles.card}>
        <div style={styles.emptyBox}>
          Loading practice questions...
        </div>
      </div>
    );
  }


  // =========================================================
  // EMPTY
  // =========================================================

  if (!currentQuestion) {
    return (
      <div style={styles.card}>
        <div style={styles.emptyBox}>
          No practice question found.
        </div>

        <button
          style={styles.secondaryButton}
          onClick={goBack}
        >
          Back
        </button>
      </div>
    );
  }


  // =========================================================
  // UI
  // =========================================================

  return (
    <div style={styles.card}>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div style={styles.topBar}>
        <div>
          <div style={styles.quizPath}>
            {practiceTopic
              ? `${t("practice.practiceQuestions")} / ${practiceTopic.topic_name}`
              : t("practice.practiceQuestions")}
          </div>

          <h2 style={styles.sectionTitle}>
            {t("practice.questionProgress")
              .replace("{current}", currentIndex + 1)
              .replace("{total}", questions.length)}
          </h2>
        </div>

        <button
          style={styles.secondaryButton}
          onClick={goBack}
          disabled={
            submittingPracticeAnswer
          }
        >
          {t("practice.back")}
        </button>
      </div>

      <div
        style={{
          marginTop: "20px",
          marginBottom: "18px",
          padding: "14px 16px",
          borderRadius: "12px",
          backgroundColor: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#334155",
          fontSize: "14px",
          lineHeight: "1.6",
        }}
      >
        <div
          style={{
            fontWeight: "800",
            color: "#1e40af",
            marginBottom: "4px",
          }}
        >
          ℹ️ {t("practice.mblExEnglishNoteTitle")}
        </div>

        <div>
          {t("practice.mblExEnglishNote")}
        </div>
      </div>

      {/* =====================================================
          QUESTION
      ===================================================== */}

      <div style={styles.questionBox}>
        <p style={styles.questionText}>
          {currentQuestion.question}
        </p>

        {Array.isArray(
          currentQuestion.media
        ) &&
          currentQuestion.media
            .filter(
              (media) =>
                media.media_type ===
                  "image" &&
                media.signed_url
            )
            .map((media) => (
              <div
                key={media.id}
                style={{
                  marginTop: "18px",
                  textAlign: "center",
                }}
              >
                <img
                  src={media.signed_url}
                  alt={
                    media.alt_text ||
                    "Question illustration"
                  }
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    width: "auto",
                    height: "auto",
                    maxHeight: "520px",
                    margin: "0 auto",
                    objectFit: "contain",
                    borderRadius: "10px",
                  }}
                />

                {media.caption && (
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "13px",
                      color: "#64748b",
                      lineHeight: "1.4",
                    }}
                  >
                    {media.caption}
                  </div>
                )}
              </div>
            ))}
      </div>


      {/* =====================================================
          OPTIONS
      ===================================================== */}

      <div style={styles.optionsWrap}>
        {currentQuestion.optionRecords.map(
          (optionRecord, index) => {
            const selected =
              selectedOptionId ===
              optionRecord.id;

            const correct =
              hasAnswered &&
              correctOptionId ===
                optionRecord.id;

            const selectedWrong =
              hasAnswered &&
              selected &&
              !correct;

            let extraStyle = {};

            if (correct) {
              extraStyle = {
                backgroundColor:
                  "#dcfce7",

                border:
                  "1px solid #86efac",
              };
            } else if (
              selectedWrong
            ) {
              extraStyle = {
                backgroundColor:
                  "#fee2e2",

                border:
                  "1px solid #fca5a5",
              };
            } else if (selected) {
              extraStyle =
                styles.selectedOption ||
                {};
            }

            return (
              <button
                key={optionRecord.id}

                disabled={
                  hasAnswered ||
                  submittingPracticeAnswer
                }

                onClick={() =>
                  handleOptionClick(
                    optionRecord
                  )
                }

                style={{
                  ...styles.optionButton,
                  ...extraStyle,

                  cursor:
                    hasAnswered ||
                    submittingPracticeAnswer
                      ? "default"
                      : "pointer",
                }}
              >
                <span
                  style={
                    styles.optionLetter
                  }
                >
                  {String.fromCharCode(
                    65 + index
                  )}
                  .
                </span>

                <span>
                  {
                    optionRecord.option_text
                  }
                </span>
              </button>
            );
          }
        )}
      </div>


      {/* =====================================================
          SAVING
      ===================================================== */}

      {submittingPracticeAnswer && (
        <div
          style={{
            marginTop: "14px",
            color: "#6b7280",
          }}
        >
          Checking answer...
        </div>
      )}


      {/* =====================================================
          ERROR
      ===================================================== */}

      {saveError && (
        <div
          style={{
            marginTop: "14px",

            padding:
              "12px 14px",

            borderRadius:
              "10px",

            backgroundColor:
              "#fee2e2",

            color:
              "#991b1b",

            fontWeight:
              "600",
          }}
        >
          {saveError}
        </div>
      )}


      {/* =====================================================
          FEEDBACK
      ===================================================== */}

      {hasAnswered && (
        <div
          style={{
            marginTop:
              "20px",

            padding:
              "14px",

            borderRadius:
              "10px",

            backgroundColor:
              isCorrect
                ? "#dcfce7"
                : "#fee2e2",

            color:
              isCorrect
                ? "#166534"
                : "#991b1b",
          }}
        >
          <div
            style={{
              fontWeight:
                "700",
            }}
          >
            {isCorrect
              ? t("practice.correct")
              : t("practice.incorrect")}
          </div>

          {!isCorrect && (
            <div
              style={{
                marginTop:
                  "6px",
              }}
            >
              {t("practice.correctAnswer")}{" "}
              <strong>
                {(() => {
                  const correctIndex =
                    currentQuestion.optionRecords.findIndex(
                      (option) =>
                        option.id ===
                        correctOptionId
                    );

                  if (
                    correctIndex < 0
                  ) {
                    return "";
                  }

                  return `${
                    String.fromCharCode(
                      65 +
                        correctIndex
                    )
                  }. ${
                    currentQuestion
                      .optionRecords[
                        correctIndex
                      ].option_text
                  }`;
                })()}
              </strong>
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          EXPLAIN UI
      ===================================================== */}

      {hasAnswered && (
        <div
          style={{
            marginTop: "20px",
            padding: "18px",
            border:
              "1px solid #e2e8f0",
            borderRadius: "14px",
            backgroundColor:
              "#f8fafc",
          }}
        >
          <div
            style={{
              fontWeight: "800",
              marginBottom: "10px",
            }}
          >
            {t("practice.needMoreExplanation")}
          </div>

          <div
            style={{
              color: "#64748b",
              fontSize: "14px",
              marginBottom: "14px",
              lineHeight: "1.5",
            }}
          >
            {t("practice.aiTutorDesc")}
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <button
              style={styles.secondaryButton}
              disabled={Boolean(
                loadingExplanationLanguage
              )}
              onClick={() =>
                handleExplain("english")
              }
            >
              {loadingExplanationLanguage ===
              "english"
                ? t("practice.loadingExplanation")
                : t("practice.explainEnglish")}
            </button>

            {language === "zh" && (
              <button
                style={styles.secondaryButton}
                disabled={Boolean(
                  loadingExplanationLanguage
                )}
                onClick={() =>
                  handleExplain("chinese")
                }
              >
                {loadingExplanationLanguage ===
                "chinese"
                  ? t("practice.loadingExplanation")
                  : t("practice.explainChinese")}
              </button>
            )}

            {language === "es" && (
              <button
                style={styles.secondaryButton}
                disabled={Boolean(
                  loadingExplanationLanguage
                )}
                onClick={() =>
                  handleExplain("spanish")
                }
              >
                {loadingExplanationLanguage ===
                "spanish"
                  ? t("practice.loadingExplanation")
                  : t("practice.explainSpanish")}
              </button>
            )}

            {language === "ru" && (
              <button
                style={styles.secondaryButton}
                disabled={Boolean(
                  loadingExplanationLanguage
                )}
                onClick={() =>
                  handleExplain("russian")
                }
              >
                {loadingExplanationLanguage ===
                "russian"
                  ? t("practice.loadingExplanation")
                  : t("practice.explainRussian")}
              </button>
            )}
          </div>

          {explanationError && (
            <div
              style={{
                marginTop: "12px",
                color: "#b91c1c",
                fontSize: "14px",
              }}
            >
              {explanationError}
            </div>
          )}

          {explanation && (
            <div
              style={{
                marginTop: "18px",
                padding: "16px",
                borderRadius: "12px",
                backgroundColor:
                  "#ffffff",
                border:
                  "1px solid #e2e8f0",
                whiteSpace:
                  "pre-wrap",
                lineHeight: "1.65",
              }}
            >
              <div
                style={{
                  fontWeight: "800",
                  marginBottom: "10px",
                }}
              >
                AI Explanation
              </div>

              {renderExplanation(explanation)}

              {explanationSource && (
                <div
                  style={{
                    marginTop: "12px",
                    color: "#94a3b8",
                    fontSize: "12px",
                  }}
                >
                  Source:{" "}
                  {explanationSource}
                  {/* ===================================================
                      EXPLANATION FEEDBACK
                  =================================================== */}

                  {explanationId && (
                    <div
                      style={{
                        marginTop: "22px",
                        paddingTop: "18px",
                        borderTop:
                          "1px solid #e2e8f0",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "700",
                          marginBottom: "10px",
                          color: "#334155",
                        }}
                      >
                        {t("practice.explanationHelpful")}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          disabled={
                            submittingExplanationFeedback
                          }
                          onClick={() =>
                            handleExplanationFeedback(
                              "helpful"
                            )
                          }
                          style={{
                            ...styles.secondaryButton,

                            backgroundColor:
                              explanationRating ===
                              "helpful"
                                ? "#dcfce7"
                                : "#ffffff",

                            border:
                              explanationRating ===
                              "helpful"
                                ? "1px solid #86efac"
                                : "1px solid #d1d5db",

                            color:
                              explanationRating ===
                              "helpful"
                                ? "#166534"
                                : "#334155",
                          }}
                        >
                          👍 {t("practice.helpful")}
                        </button>

                        <button
                          disabled={
                            submittingExplanationFeedback
                          }
                          onClick={() =>
                            handleExplanationFeedback(
                              "not_helpful"
                            )
                          }
                          style={{
                            ...styles.secondaryButton,

                            backgroundColor:
                              explanationRating ===
                              "not_helpful"
                                ? "#fee2e2"
                                : "#ffffff",

                            border:
                              explanationRating ===
                              "not_helpful"
                                ? "1px solid #fca5a5"
                                : "1px solid #d1d5db",

                            color:
                              explanationRating ===
                              "not_helpful"
                                ? "#991b1b"
                                : "#334155",
                          }}
                        >
                          👎 {t("practice.notHelpful")}
                        </button>
                      </div>


                      {submittingExplanationFeedback && (
                        <div
                          style={{
                            marginTop: "10px",
                            color: "#64748b",
                            fontSize: "13px",
                          }}
                        >
                          {t("practice.savingFeedback")}
                        </div>
                      )}


                      {explanationFeedbackMessage && (
                        <div
                          style={{
                            marginTop: "10px",
                            color: "#166534",
                            fontSize: "13px",
                            fontWeight: "600",
                          }}
                        >
                          {
                            explanationFeedbackMessage
                          }
                        </div>
                      )}


                      {explanationFeedbackError && (
                        <div
                          style={{
                            marginTop: "10px",
                            color: "#b91c1c",
                            fontSize: "13px",
                          }}
                        >
                          {
                            explanationFeedbackError
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          NAVIGATION
      ===================================================== */}

      <div style={styles.navButtons}>
        <button
          style={{
            ...styles.secondaryButton,

            ...(currentIndex === 0
              ? styles.disabledButton
              : {}),
          }}

          disabled={
            currentIndex === 0 ||
            submittingPracticeAnswer
          }

          onClick={
            goPrevious
          }
        >
          {t("practice.previous")}
        </button>

        <button
          style={{
            ...styles.primaryButton,

            ...(!hasAnswered
              ? styles.disabledButton
              : {}),
          }}

          disabled={
            !hasAnswered ||
            submittingPracticeAnswer
          }

          onClick={() => {
            if (
              currentIndex ===
              questions.length - 1
            ) {
              finishRound();
            } else {
              goNext();
            }
          }}
        >
          {currentIndex ===
          questions.length - 1
            ? t("practice.finishRound")
            : t("practice.next")}
        </button>
      </div>
    </div>
  );
}